// Support page — reachable at /support with or without sign-in
// (App.tsx routes it before the auth gate, mirroring PrivacyPolicy).
// This URL goes in the "Support URL" field in App Store Connect →
// Version page. Apple reviewers visit it before approving.
//
// Keep the contact channels accurate — Apple sometimes spot-checks
// that the email actually receives mail.

const LAST_UPDATED = "June 24, 2026";

export function Support() {
  return (
    <div className="min-h-full bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-10 md:py-16">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
            Support
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Last updated: {LAST_UPDATED}
          </p>
        </header>

        <article className="prose prose-slate max-w-none space-y-6 text-[15px] text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              Contact
            </h2>
            <p>
              ShiftManager is operated by a single developer. The
              fastest way to reach us is email — we respond within
              one business day for support questions, and within
              30 days for formal data-subject requests (deletion,
              export, correction).
            </p>
            <p className="font-mono text-sm">
              <a
                href="mailto:jamesleonbond@gmail.com"
                className="text-sky-700 hover:underline"
              >
                jamesleonbond@gmail.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              Common questions
            </h2>

            <h3 className="text-base font-semibold text-slate-900 mt-4">
              I forgot my password.
            </h3>
            <p>
              On the sign-in screen, tap <em>Forgot password</em>{" "}
              and enter the email your supervisor invited you with.
              You'll get a reset link within a minute. If it doesn't
              arrive, check your spam folder, then email us with the
              address you tried.
            </p>

            <h3 className="text-base font-semibold text-slate-900 mt-4">
              I'm a supervisor and want to start a new agency.
            </h3>
            <p>
              Subscriptions are managed on the web. Visit{" "}
              <a
                href="https://pdshiftmanager.com/"
                className="text-sky-700 hover:underline"
              >
                pdshiftmanager.com
              </a>{" "}
              and tap <em>Start your agency</em> from the sign-in
              screen. After Stripe checkout you'll get an email
              with the welcome link.
            </p>

            <h3 className="text-base font-semibold text-slate-900 mt-4">
              I want to delete my account.
            </h3>
            <p>
              In the app, open the sidebar, tap your profile, then{" "}
              <em>Request account deletion</em>. Your agency's owner
              or a Level 1 supervisor will approve the request and
              your account will be deactivated. Schedule history is
              preserved for the agency's records.
            </p>

            <h3 className="text-base font-semibold text-slate-900 mt-4">
              I'm not receiving push notifications.
            </h3>
            <p>
              On iPhone: open iOS{" "}
              <em>Settings → ShiftManager → Notifications</em> and
              make sure Allow Notifications is on. On desktop
              browsers: open ShiftManager → Settings → Browser
              notifications → Enable. If push still doesn't land,
              email us with the browser or iOS version you're on
              and we'll dig into it.
            </p>

            <h3 className="text-base font-semibold text-slate-900 mt-4">
              How is my agency's data kept private?
            </h3>
            <p>
              Every database row is scoped to a single agency via
              row-level security. Users from one agency can't read,
              write, or even see the existence of another agency's
              data. The full data-handling story is in our{" "}
              <a href="#/privacy" className="text-sky-700 hover:underline">
                Privacy Policy
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              Status and outages
            </h2>
            <p>
              ShiftManager runs on Supabase (database + auth + edge
              functions) and GitHub Pages (web app). Brief outages
              of either upstream service can affect the app. If you
              can't reach ShiftManager at all, check{" "}
              <a
                href="https://status.supabase.com"
                className="text-sky-700 hover:underline"
              >
                status.supabase.com
              </a>{" "}
              and{" "}
              <a
                href="https://www.githubstatus.com"
                className="text-sky-700 hover:underline"
              >
                githubstatus.com
              </a>{" "}
              before emailing — chances are it's a known incident.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              Feature requests and bug reports
            </h2>
            <p>
              Same email as above. Be specific about what you
              expected to happen vs what actually happened, and
              include your build SHA (visible at the bottom of the
              sidebar) so we know which version you're on.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
}
